import React, { useEffect, useMemo, useState } from "react";
import { GlobalContext } from "../../../Context/GlobalContext";
import { Calendar, RefreshCcw, Trash2, X } from "lucide-react";
import { toast } from "react-hot-toast";

import useSettlement from "../../Services/OrderServices/SettlementServices/useSettlement";

const toDateStr = (d) => {
  try {
    return new Date(d).toISOString().split("T")[0];
  } catch {
    return "";
  }
};

export default function SettlementInvoicesTab({
  userID,
  defaultFromDate = "",
  defaultToDate = "",
}) {
  const {
    getSettlementInvoices,
    generateSettlementInvoices,
    deleteUserSettlement,
  } = useSettlement();



  const today = useMemo(() => toDateStr(new Date()), []);
  const [fromDate, setFromDate] = useState(defaultFromDate || today);
  const [toDate, setToDate] = useState(defaultToDate || today);

  const [loading, setLoading] = useState(false);
  const [settlementInvoices, setSettlementInvoices] = useState([]);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    invoice: null,
  });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const getSettlementInvoicesApi = async () => {
    if (!userID) return;

    setLoading(true);

    try {
      const result =
        await getSettlementInvoices(userID);

      if (result.success) {
        setSettlementInvoices(result.data);
      } else {
        setSettlementInvoices([]);
      }
    } catch (error) {
      console.log(
        "getSettlementInvoicesApi error:",
        error
      );
      setSettlementInvoices([]);
    } finally {
      setLoading(false);
    }
  };


  const generateSettlementInvoicesApi = async () => {
    if (!userID) {
      toast.error("User ID missing.");
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      toast.error(
        "From Date cannot be after To Date."
      );
      return;
    }

    setLoading(true);

    try {
      const result =
        await generateSettlementInvoices({
          userid: userID,
          fromDate,
          toDate,
        });

      if (result.success) {
        toast.success(result.message);
        await getSettlementInvoicesApi();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.log(
        "generateSettlementInvoicesApi error:",
        error
      );
      toast.error("Error generating invoices.");
    } finally {
      setLoading(false);
    }
  };
  const deleteSettlementInvoiceApi = async () => {
    const invoice = deleteModal.invoice;

    if (!invoice?.id || !invoice?.fk_userid) {
      toast.error("Invoice ID or User ID missing.");
      return;
    }

    setDeleteLoading(true);

    try {
      const result = await deleteUserSettlement({
        id: invoice.id,
        fkuserid: invoice.fk_userid,
      });

      if (result.success) {
        toast.success(result.message || "Deleted successfully");
        setDeleteModal({ open: false, invoice: null });
        await getSettlementInvoicesApi();
      } else {
        toast.error(result.message || "Delete failed");
      }
    } catch (error) {
      console.log("deleteSettlementInvoiceApi error:", error);
      toast.error("Error deleting invoice.");
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    if (userID)
      getSettlementInvoicesApi();

  }, [userID]);

  return (
    <div className="rounded-2xl border bg-white">
      {/* Controls */}
      <div className="border-b p-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            <div className="mb-1 flex items-center gap-1 text-xs text-gray-500">
              <Calendar size={14} /> From
            </div>
            <input
              type="date"
              className="h-10 w-[180px] rounded-xl border px-3 text-sm outline-none"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              disabled={loading}
            />
          </label>

          <label className="text-sm">
            <div className="mb-1 flex items-center gap-1 text-xs text-gray-500">
              <Calendar size={14} /> To
            </div>
            <input
              type="date"
              className="h-10 w-[180px] rounded-xl border px-3 text-sm outline-none"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              disabled={loading}
            />
          </label>

          <button
            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-[#01C37D] px-4 text-sm font-semibold text-white hover:bg-emerald-600 disabled:opacity-50"
            onClick={generateSettlementInvoicesApi}
            disabled={loading}
            title="Generate settlement invoices"
          >
            {loading ? "Generating..." : "Generate"}
          </button>

          <button
            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border px-4 text-sm hover:bg-gray-50 disabled:opacity-50"
            onClick={getSettlementInvoicesApi}
            disabled={loading}
            title="Refresh invoices list"
          >
            <RefreshCcw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="max-h-[70vh] overflow-auto">
        <table className="min-w-full text-left text-xs">
          <thead className="sticky top-0 z-10 bg-[#01C37D] text-white">
            <tr className="text-center">
              <th className="px-2 py-2 font-semibold whitespace-nowrap">S.No.</th>
              <th className="px-2 py-2 font-semibold whitespace-nowrap">User ID</th>
              <th className="px-2 py-2 font-semibold whitespace-nowrap">Invoice No</th>
              <th className="px-2 py-2 font-semibold whitespace-nowrap">Invoice Name</th>
              <th className="px-2 py-2 font-semibold whitespace-nowrap">From</th>
              <th className="px-2 py-2 font-semibold whitespace-nowrap">To</th>
              <th className="px-2 py-2 font-semibold whitespace-nowrap">Action</th>
            </tr>
          </thead>

          <tbody>
            {!loading && settlementInvoices.length === 0 && (
              <tr className="border-t">
                <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                  No invoices found.
                </td>
              </tr>
            )}

            {settlementInvoices.map((inv, index) => (
              <tr  key={inv.id || index} className="border-t text-center">
                <td className="px-2 py-1.5">{index + 1}</td>
                <td className="px-2 py-1.5 fw-semibold">{inv.fk_userid}</td>
                <td className="px-2 py-1.5 fw-semibold">{inv.invoice_number}</td>
                <td className="px-2 py-1.5 fw-semibold">{inv.invoice_name}</td>
                <td className="px-2 py-1.5">{inv.from_date}</td>
                <td className="px-2 py-1.5">{inv.to_date}</td>

                <td className="px-2 py-1.5">
                  <div className="flex items-center justify-center gap-2">
                    {inv.invoice_url ? (
                      <a
                        href={inv.invoice_url}
                        download="invoice"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                      >
                        Download
                      </a>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}

                    <button
                      type="button"
                      onClick={() =>
                        setDeleteModal({
                          open: true,
                          invoice: inv,
                        })
                      }
                      className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 disabled:opacity-50"
                      disabled={loading}
                      title="Delete invoice"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {loading && (
              <tr className="border-t">
                <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="border-t px-3 py-2 text-[11px] text-gray-500">
        Generate invoices for selected dates, then download from the list.
      </div>
      {deleteModal.open && (
  <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
    <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div>
          <h3 className="text-sm font-bold text-gray-900">
            Delete Invoice
          </h3>
          <p className="text-xs text-gray-500">
            This action cannot be undone.
          </p>
        </div>

        <button
          type="button"
          onClick={() =>
            setDeleteModal({
              open: false,
              invoice: null,
            })
          }
          disabled={deleteLoading}
          className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        >
          <X size={18} />
        </button>
      </div>

      <div className="px-4 py-4 text-sm text-gray-700">
        Are you sure you want to delete invoice{" "}
        <span className="font-semibold text-gray-900">
          {deleteModal.invoice?.invoice_number || "—"}
        </span>
        ?
      </div>

      <div className="flex justify-end gap-2 border-t px-4 py-3">
        <button
          type="button"
          onClick={() =>
            setDeleteModal({
              open: false,
              invoice: null,
            })
          }
          disabled={deleteLoading}
          className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={deleteSettlementInvoiceApi}
          disabled={deleteLoading}
          className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
        >
          {deleteLoading ? "Deleting..." : "Delete"}
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
}
